import { useToast } from '@app/core/notifications';
import { auditReports } from '@mi-front/api-clients';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { aodMissingMaterialsMessages } from '../../../../../messages';
import { AcceptRun } from './accept/AcceptRun';
import { RefreshRun } from './refresh/RefreshRun';
import { ReprocessRun } from './reprocess/ReprocessRun';

/**
 * Orchestrates refresh / accept / reprocess actions for an AOD run.
 */
export function AdvancedRunActions({ run }: { run: auditReports.AodRun }) {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const { notifyError } = useToast();
  const [activeAction, setActiveAction] = useState<string | null>(null);

  const executeAction = async (actionType: string, actionPromise: Promise<unknown>) => {
    if (activeAction !== null) return; // Prevent multiple actions

    setActiveAction(actionType);
    try {
      await actionPromise;

      // Invalidate AOD run queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: auditReports.getManyBaseAodRunsControllerAodRunQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: auditReports.getOneBaseAodRunsControllerAodRunQueryKey(run.id),
      });
    } catch (error) {
      console.error(`Action ${actionType} failed:`, error);

      if (actionType === 'reprocess') {
        const isMissingMaterialsRejection =
          isAxiosError(error) &&
          error.response?.status === 400 &&
          typeof error.response.data?.message === 'string' &&
          /no linked materials|missing materials/i.test(error.response.data.message);

        // Separate formatMessage calls so the extractor sees static descriptors (no ternary).
        if (isMissingMaterialsRejection) {
          notifyError(formatMessage(aodMissingMaterialsMessages.reprocessError));
        } else {
          notifyError(formatMessage(aodMissingMaterialsMessages.reprocessErrorGeneric));
        }
      }
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} mt={1}>
      <RefreshRun
        run={run}
        isDisabled={activeAction !== null}
        isLoading={activeAction === 'refresh'}
        executeAction={(promise: Promise<unknown>) => executeAction('refresh', promise)}
      />
      <AcceptRun
        run={run}
        isDisabled={activeAction !== null}
        isLoading={activeAction === 'accept'}
        executeAction={(promise: Promise<unknown>) => executeAction('accept', promise)}
      />
      <ReprocessRun
        run={run}
        isDisabled={activeAction !== null}
        isLoading={activeAction === 'reprocess'}
        executeAction={(promise: Promise<unknown>) => executeAction('reprocess', promise)}
      />
    </Box>
  );
}
